// src/pages/admin/AdminFacturasSaaS.js
//
// Facturación SaaS a tenderos.
// D-24: las mensualidades salen de la suscripción de cada negocio (corte
// día 1, plazo de pago, bloqueo por mora). Aquí se consultan, se descarga
// el PDF, se reenvía por correo, se registra un pago manual, se extiende el
// plazo o se anula.

import React, { useEffect, useState } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField,
  Button, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Tooltip, Alert, LinearProgress, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PlayArrowIcon   from '@mui/icons-material/PlayArrow';
import CheckIcon       from '@mui/icons-material/Check';
import BlockIcon       from '@mui/icons-material/Block';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { facturas as api } from '../../api/tesoreriaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const nombreProducto = { NEGOCIO: 'Negocio', LOCAL: 'Local', HOLAVECI_360: 'HolaVeci 360°' };
const nombreTipo = { MENSUALIDAD: 'Mensualidad', PRIMER_MES: 'Primer mes', AJUSTE_360: 'Ajuste 360°' };
const fechaCo = (iso) => iso
  ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('es-CO', {
      timeZone: 'America/Bogota', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—';
const colorEstado = (e) => e==='PAGADA'?'success':e==='PENDIENTE'?'warning':e==='VENCIDA'?'error':'default';

export default function AdminFacturasSaaS() {
  const { notificar } = useApp();
  const hoy = new Date();
  const [filtros, setFiltros] = useState({
    anio: hoy.getFullYear(), mes: hoy.getMonth() + 1, estado: '',
  });
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogPago, setDialogPago] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const q = { anio: filtros.anio, mes: filtros.mes };
      if (filtros.estado) q.estado = filtros.estado;
      const r = await api.listar(q);
      setLista(r || []);
    } catch (e) {
      notificar(e.mensajeUsuario || 'No se pudo cargar.', 'error');
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [filtros.anio, filtros.mes, filtros.estado]); // eslint-disable-line

  const generar = async () => {
    if (!window.confirm('Ejecutar ahora el ciclo de cobro del mes en curso (mensualidades, recordatorios y suspensión por mora)?')) return;
    try {
      const r = await api.generarPeriodo(filtros.anio, filtros.mes);
      notificar(r.mensaje, 'success');
      cargar();
    } catch (e) {
      notificar(e.mensajeUsuario || 'Error generando.', 'error');
    }
  };

  const anular = async (f) => {
    const motivo = window.prompt(`Anular factura ${f.numeroFactura}. Motivo:`);
    if (!motivo) return;
    try { await api.anular(f.facturaId, motivo); notificar('Anulada.', 'success'); cargar(); }
    catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
  };

  const reenviar = async (f) => {
    try { const r = await api.reenviar(f.facturaId); notificar(r.mensaje, 'success'); cargar(); }
    catch (e) { notificar(e.mensajeUsuario || 'No se pudo enviar.', 'error'); }
  };

  const extender = async (f) => {
    const dias = Number(window.prompt(`Días adicionales para pagar ${f.numeroFactura} (1 a 30):`, '2'));
    if (!dias) return;
    try { const r = await api.extenderPlazo(f.facturaId, dias); notificar(r.mensaje, 'success'); cargar(); }
    catch (e) { notificar(e.mensajeUsuario || 'No se pudo extender.', 'error'); }
  };

  const totales = lista.reduce((acc, f) => {
    if (f.estado === 'PAGADA') { acc.pagadas += f.total; acc.nPagadas++; }
    else if (f.estado === 'PENDIENTE') { acc.pendientes += f.total; acc.nPend++; }
    return acc;
  }, { pagadas: 0, pendientes: 0, nPagadas: 0, nPend: 0 });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Facturación SaaS</Typography>
          <Typography variant="body2" color="text.secondary">
            Facturas mensuales a los tenderos afiliados.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={2}>
              <TextField select fullWidth size="small" label="Año" value={filtros.anio}
                onChange={e => setFiltros(f => ({ ...f, anio: Number(e.target.value) }))}>
                {[2024,2025,2026,2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select fullWidth size="small" label="Mes" value={filtros.mes}
                onChange={e => setFiltros(f => ({ ...f, mes: Number(e.target.value) }))}>
                {Array.from({length:12}, (_,i)=>i+1).map(m =>
                  <MenuItem key={m} value={m}>{String(m).padStart(2,'0')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth size="small" label="Estado" value={filtros.estado}
                onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="PENDIENTE">Pendientes</MenuItem>
                <MenuItem value="PAGADA">Pagadas</MenuItem>
                <MenuItem value="VENCIDA">Vencidas</MenuItem>
                <MenuItem value="ANULADA">Anuladas</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={5} sx={{ textAlign: 'right' }}>
              <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={generar}>
                Ejecutar ciclo de cobro
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Pagadas</Typography>
            <Typography variant="h5" color="success.main">{fmtCOP(totales.pagadas)}</Typography>
            <Typography variant="caption">{totales.nPagadas} facturas</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Pendientes</Typography>
            <Typography variant="h5" color="warning.main">{fmtCOP(totales.pendientes)}</Typography>
            <Typography variant="caption">{totales.nPend} facturas</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {cargando ? <LinearProgress /> : lista.length === 0 ? (
        <Alert severity="info">Sin cuentas de cobro para este período.</Alert>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Base</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Emitida</TableCell>
                <TableCell>Límite de pago</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map(f => (
                <TableRow key={f.facturaId} hover>
                  <TableCell><Typography variant="caption">{f.numeroFactura}</Typography></TableCell>
                  <TableCell>{f.nombreComercial || f.empresa}</TableCell>
                  <TableCell><Chip label={nombreProducto[f.producto]} size="small" /></TableCell>
                  <TableCell>
                    <Typography variant="caption">{nombreTipo[f.tipo] || f.tipo}</Typography>
                    {f.pedidosCantidad > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Plan {fmtCOP(f.valorPlan)} + {f.pedidosCantidad} pedidos {fmtCOP(f.valorPedidos)}
                      </Typography>
                    )}
                    {f.detalle && <Typography variant="caption" display="block" color="text.secondary">{f.detalle}</Typography>}
                  </TableCell>
                  <TableCell align="right">{fmtCOP(f.baseGravable)}</TableCell>
                  <TableCell align="right">{fmtCOP(f.iva)}</TableCell>
                  <TableCell align="right"><b>{fmtCOP(f.total)}</b></TableCell>
                  <TableCell>{f.fechaEmision?.substring(0,10)}</TableCell>
                  <TableCell>{f.tipo === 'MENSUALIDAD' ? fechaCo(f.fechaLimitePago) : '—'}</TableCell>
                  <TableCell align="center">
                    <Chip label={f.estado} size="small" color={colorEstado(f.estado)} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Ver PDF">
                      <IconButton size="small" onClick={() => api.abrirPdf(f.facturaId).catch(e => notificar(e.message, 'error'))}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={f.correoEnviadoEn ? `Reenviar (último envío ${fechaCo(f.correoEnviadoEn)})` : 'Enviar por correo'}>
                      <IconButton size="small" onClick={() => reenviar(f)}>
                        <ForwardToInboxIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {f.tipo === 'MENSUALIDAD' && (f.estado === 'PENDIENTE' || f.estado === 'VENCIDA') && (
                      <Tooltip title="Extender plazo">
                        <IconButton size="small" color="info" onClick={() => extender(f)}>
                          <EventRepeatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(f.estado === 'PENDIENTE' || f.estado === 'VENCIDA') && (
                      <>
                        <Tooltip title="Marcar pagada">
                          <IconButton size="small" color="success" onClick={() => setDialogPago(f)}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Anular">
                          <IconButton size="small" color="error" onClick={() => anular(f)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <DialogPago factura={dialogPago} onCerrar={() => setDialogPago(null)}
                  onPagada={() => { setDialogPago(null); cargar(); }} />
    </Container>
  );
}

export function DialogPago({ factura, onCerrar, onPagada }) {
  const { notificar } = useApp();
  const [f, setF] = useState({ medioPago: 'TRANSFERENCIA', referenciaExterna: '',
                                comprobanteUrl: '', notas: '' });
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      await api.marcarPagada(factura.facturaId, f);
      notificar('Factura marcada como pagada.', 'success');
      onPagada();
    } catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
    finally { setG(false); }
  };
  return (
    <Dialog open={!!factura} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Marcar como pagada</DialogTitle>
      <DialogContent>
        {factura && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>{factura.numeroFactura}</b> · {factura.nombreComercial || factura.empresa} · {fmtCOP(factura.total)}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField select fullWidth label="Medio de pago" value={f.medioPago}
                     onChange={e => setF({ ...f, medioPago: e.target.value })}>
            <MenuItem value="WOMPI_TARJETA">Wompi (tarjeta)</MenuItem>
            <MenuItem value="WOMPI_NEQUI">Wompi (Nequi)</MenuItem>
            <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
            <MenuItem value="EFECTIVO">Efectivo</MenuItem>
            <MenuItem value="OTRO">Otro</MenuItem>
          </TextField>
          <TextField fullWidth label="Referencia externa" value={f.referenciaExterna}
                     onChange={e => setF({ ...f, referenciaExterna: e.target.value })} />
          <TextField fullWidth label="URL comprobante (opcional)" value={f.comprobanteUrl}
                     onChange={e => setF({ ...f, comprobanteUrl: e.target.value })} />
          <TextField fullWidth multiline rows={2} label="Notas" value={f.notas}
                     onChange={e => setF({ ...f, notas: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>
          {g ? 'Guardando…' : 'Confirmar pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
